import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { OtkList } from 'src/app/models/user-item-list';
import { Record } from 'src/app/models/record';
import { AuthService } from 'src/app/services/auth.service';
import { UserService } from 'src/app/services/user.service';
import { LocaleService } from 'src/app/services/locale.service';
import { Util } from 'src/app/util';
import { Rating } from 'src/app/models/rating';
import { SearchModel, SearchState } from 'src/app/models/search-model';
import { User } from '../../models/user';
import { PlayerUrlAnnotationService } from '../../services/api/player-url-annotation.service';
import { UserListButtonConfiguration } from 'src/app/models/buttons-config';
import { ConfigService } from 'src/app/services/config.service';
import { paginationState } from '../record-pagination/record-pagination-state';
import { ToastService } from '../../_core/services/toast.service';
import { UserFacade } from 'src/app/_core/state/user/user.facade';
import { AnalyticsService } from '../../_core/analytics/analytics.service';

@Component({
  selector: 'app-public-list',
  templateUrl: './public-list.component.html',
  styleUrls: ['./public-list.component.scss'],
})
export class PublicListComponent implements OnInit {
  private readonly toastService = inject(ToastService);
  private readonly userFacade = inject(UserFacade);
  private readonly analyticsService = inject(AnalyticsService);

  readonly showEmptyListDialog = signal<boolean>(false);
  readonly showDeleteListDialog = signal<boolean>(false);
  readonly descriptionToDeleteListDialog = signal<string>('');

  listId: number;
  list: OtkList;
  records: Record[] = [];
  ratings: Rating[] = [];
  forbidden: boolean;
  isOwner: boolean;
  back: boolean;
  readOnly = true;
  showFollowListButton: boolean;
  showUnfollowListButton: boolean;
  showTopNavigation = false;
  state: SearchState;
  record: Record;
  slug: string;

  hasLoadMore: boolean;
  waiting: boolean;
  resultLimit = 9;
  offset = 0;
  user: User;

  buttonsConfig = new UserListButtonConfiguration(this.configService.get().showReturnLoanButton);

  readonly publicState = paginationState;

  constructor(
    private router: Router,
    private localeService: LocaleService,
    private authService: AuthService,
    private userService: UserService,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private playerUrlAnnotationService: PlayerUrlAnnotationService,
    private configService: ConfigService
  ) {
    this.readOnly = !this.authService.isAuthenticated();

    this.state = this.router.getCurrentNavigation().extras.state as SearchState;
    paginationState.set(this.state);

    this.showTopNavigation = this.state && this.state.index !== undefined;
    this.back = history.length > 0;
    this.user = this.userFacade.user;
  }

  ngOnInit() {
    this.waiting = true;
    this.route.params.subscribe(
      (params) => {
        this.slug = params.slug;
        this.listId = Number(this.slug.split('-').pop());

        this.apiService.getPublicList(this.listId).subscribe(
          (_list) => {
            this.list = _list;
            this.descriptionToDeleteListDialog.set(this.getDeleteListLabel());
            this.offset = 0;
            if (_list.privateList) {
              this.forbidden = !this.authService.isAuthenticated() || _list.userId !== this.userService.getUserId();

              if (this.forbidden) {
                this.toastService.showToast('messages.lists.forbidden', 'error');
                return this.router.navigate(['/']);
              }
            }

            this.isOwner = this.authService.isAuthenticated() && this.list.userId === this.userService.getUserId();
            this.refreshListFollowingFeaturesUI();
            this.getRecords(true);
            this.analyticsService.sendUserActionEvent('list_detail', true);
          },
          (_err) => {
            this.waiting = false;
          }
        );
      },
      (_err) => {
        this.waiting = false;
      }
    );
  }

  deleteList() {
    if (this.list.userId !== this.userService.getUserId()) {
      return;
    }

    this.showDeleteListDialog.set(false);
    this.apiService.deleteList(this.userService.getUserId(), this.list.id).subscribe({
      next: (deletedList) => {
        this.userService.lists = this.userService.lists.filter((l) => l.id !== deletedList.id);
        this.toastService.showToast('messages.lists.remove');
        this.router.navigate(['/user/lists']);
        this.analyticsService.sendUserActionEvent('delete_list', true);
      },
      error: () => this.analyticsService.sendUserActionEvent('delete_list', false),
    });
  }

  emptyList() {
    if (this.list.userId !== this.userService.getUserId()) {
      return;
    }

    this.showEmptyListDialog.set(false);
    this.apiService.emptyList(this.userService.getUserId(), this.list.id).subscribe(() => {
      this.list.items = [];
      this.toastService.showToast('messages.lists.empty');
    });
  }

  editList() {
    this.userService.editList(this.list);
  }

  addTitles() {
    this.router.navigate(['/search', 'lists', this.list.id]);
  }

  removeRecordFromList(recordId: string) {
    this.userService.removeRecordFromList(this.list, recordId).subscribe(() => {
      const i = this.records.findIndex((r) => r.id === recordId);
      this.records.splice(i, 1);
    });
  }

  copyToClipboard() {
    const slug = Util.slugify(this.list.name) + '-' + this.list.id;
    const url = new URL(Util.getOrigin() + '/list/' + slug);
    Util.copyToClipboard(url.href);
    this.toastService.showToast('messages.copied', 'info');
    this.analyticsService.sendUserActionEvent('share_list', true);
  }

  loadMore() {
    if (!this.list) {
      return;
    }
    this.offset = this.offset + this.resultLimit;
    this.getRecords();
  }

  getRatingByRecord(record: Record): Rating {
    return this.ratings.find((r) => [record.id, record.isbn].includes(r.id));
  }

  followList() {
    this.userService.followList(this.list).subscribe((_) => this.refreshListFollowingFeaturesUI());
  }

  unfollowList() {
    this.userService.unfollowList(this.list).subscribe((_) => this.refreshListFollowingFeaturesUI());
  }

  getState(index: number) {
    const searchModel = new SearchModel({
      recordIds: this.list.items.map((record) => record.recordId),
    });
    const page = Math.floor(index / this.resultLimit);
    const stateOffset = this.resultLimit * page;
    searchModel.offset = stateOffset;
    searchModel.limit = this.resultLimit;
    const i = index - stateOffset;
    const stateRecords = this.records.slice(stateOffset, stateOffset + this.resultLimit);

    return {
      index: i,
      page: page,
      pageSize: this.resultLimit,
      records: stateRecords.map((_record) => ({
        id: _record.id,
        title: _record.title,
        wishListInfo: _record.wishListInfo,
      })),
      total: this.list.items.length,
      searchModel: searchModel,
      from: 'LIST_SCREEN',
      genesis: this.publicState() && this.publicState().genesis ? { ...this.publicState().genesis } : new SearchModel(),
      listSlug: this.slug,
      prevState: this.publicState(),
    } as SearchState;
  }

  private refreshListFollowingFeaturesUI() {
    this.showFollowListButton =
      this.authService.isAuthenticated() &&
      this.list.userId !== this.userService.getUserId() &&
      !this.list.followedByActiveUser;
    this.showUnfollowListButton = this.authService.isAuthenticated() && this.list.followedByActiveUser;
  }

  private getRecords(refresh: boolean = false) {
    const limit = this.offset + this.resultLimit;
    const recordIds = this.list.items.slice(this.offset, limit).map((record) => record.recordId);

    this.hasLoadMore = limit < this.list.items.length;
    this.waiting = true;
    if (recordIds && recordIds.length > 0) {
      this.getRecordsByRecorIds(recordIds, refresh);
    } else {
      this.waiting = false;
    }
  }

  private getRecordsByRecorIds(recordIds: string[], refesh: boolean) {
    this.apiService.getRecords({ recordIds: recordIds }).subscribe((result) => {
      this.waiting = false;
      if (refesh) {
        this.records = result.records;
      } else {
        this.records.push(...result.records);
      }

      const byIsbn = result.records.filter((r) => r.isbn).map((r) => r.isbn);
      const byRecordId = result.records.filter((r) => !r.isbn).map((r) => r.id);

      let userId: string;

      if (this.authService.isAuthenticated()) {
        userId = this.userService.getUserId();
      }

      setTimeout(() => {
        Util.runAction(this.user, this.playerUrlAnnotationService, this.records[0]);
      }, 1000);

      this.apiService.getRecordRatings(byRecordId, byIsbn, userId).subscribe((ratings) => {
        this.records.forEach((record) => {
          const rating = ratings.find((r) => [record.id, record.isbn].includes(r.id));
          record.rating = rating;
        });
      });
    });
  }

  private getDeleteListLabel() {
    if (this.list?.followers > 0) {
      return 'user.lists.dialogs.deleteWhenFollowed.body';
    }

    return 'user.lists.dialogs.deleteList.body';
  }
}
